import { debounce } from "lodash-es";
import { useCallback, useEffect, useRef } from "react";
import { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import { copy } from "shared/assets";
import { blurActiveElement } from "shared/utils";
import {
  DefaultControl,
  DefaultOption,
  HiddenControl,
  SelectDropdownProps,
} from "../components";

export const useSelectDropdownLogic = ({
  options,
  Option = DefaultOption,
  selectedValue,
  autocomplete,
  disabled,
  isFocused,
  isSearchable,
  isCreatable,
  readOnly,
  handleClose = () => {},
  handleBlur = () => {},
  onChange: handleChange = () => {},
  onInputChange,
}: Partial<SelectDropdownProps>) => {
  const scrollToActiveItem = () => {
    const menu = document.querySelector(".select__menu-list");

    if (menu) {
      const selectedItem = menu.querySelector(".select__option--is-selected");
      menu.scrollTop = (selectedItem as HTMLElement)?.offsetTop;
    }
  };

  const prevValue = useRef<string>();

  // blur and close dropdown if value changes
  useEffect(() => {
    if (prevValue.current === selectedValue?.value) {
      return;
    }

    // set previous value
    prevValue.current = selectedValue?.value;

    // blur active element (hack to trigger blur)
    blurActiveElement();

    // close dropdown after blurring active element
    handleClose();
  }, [selectedValue?.value, handleClose]);

  const filterOptions = useCallback(
    ({ label, data, value }: FilterOptionOption<any>, inputValue: string) => {
      if (data.alwaysShow) return true;

      return `${label} ${value} ${data.searchableLabel}`
        .toLowerCase()
        .includes(inputValue.toLowerCase());
    },
    []
  );

  const getSelectProps = useCallback(
    ({
      onChange,
      onBlur,
    }: {
      onChange: (data: any) => void;
      onBlur: () => void;
    }) => ({
      autoFocus: isFocused,
      value: selectedValue,
      placeholder: isCreatable ? copy.form.searchOrCreate : copy.form.search,
      options: options,
      onChange: (newValue: any) => {
        if (newValue && newValue.onClick) {
          newValue && newValue.onClick();
        } else {
          handleChange(newValue, prevValue.current);
          onChange(newValue.value);
        }

        onBlur();
        handleClose();
      },
      onBlur: () => {
        onBlur();
        handleBlur();
        handleClose();
      },
      onMenuOpen: scrollToActiveItem,
      isDisabled: disabled || readOnly,
      autoComplete: autocomplete,
      components: {
        Control: isSearchable ? DefaultControl : HiddenControl,
        DropdownIndicator: null,
        IndicatorSeparator: null,
        Option,
      },
      filterOption: filterOptions,
      onInputChange: onInputChange ? debounce(onInputChange, 500) : undefined,
    }),
    [
      isFocused,
      selectedValue,
      isCreatable,
      options,
      disabled,
      readOnly,
      autocomplete,
      isSearchable,
      Option,
      filterOptions,
      onInputChange,
      handleClose,
      handleChange,
      handleBlur,
    ]
  );

  return { getSelectProps };
};
